import * as styles from '../FirstResponder.module.css';

/*
 * Non-ready frames (loading / error / unauthorized) and the two safety
 * takeovers. The safety states are a closed union: 'unknown' is its own
 * takeover and never renders as clear or breached.
 */

export const LoadingFrame = () => (
  <div className={styles.card} data-testid="flp-loading">
    <div className={styles.section}>
      <span className={styles.muted}>Loading First Responder…</span>
    </div>
  </div>
);

export const ErrorFrame = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <div className={styles.card} data-testid="flp-error" role="alert">
    <div className={styles.section}>
      <div className={styles.strong}>Couldn&apos;t load First Responder.</div>
      <div className={`${styles.muted} ${styles.small}`}>{message}</div>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnOutline}`}
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    </div>
  </div>
);

/*
 * GRAVY-10373 F9: "Restricted access." alone left the operator to guess whether the console was
 * broken, the pilot was hidden, or they were simply not allowed. This frame is now reached by a
 * 403 as well as a 401 — but the two share only the frame, never the sentence:
 *
 *   'session' (401) — the token refresh failed and the session is gone. The fix is to sign in
 *                     again, and it is available to the operator themselves.
 *   'role'    (403) — the signed-in role may not view this console. Signing in again changes
 *                     nothing; the fix is someone else's grant. Since Helpdesk may now open the
 *                     console read-only (GRAVY-10816 bug 2), a 403 here is a customer admin or an
 *                     unknown role.
 *
 * One sentence covering both would send half of the operators who see it to a fix that cannot
 * work — the same misleading generic error this fix exists to remove, just relocated.
 */
export type UnauthorizedReason = 'session' | 'role';

const UNAUTHORIZED_REASON_COPY: Record<UnauthorizedReason, string> = {
  session: 'Your session has ended, sign in again.',
  role: "Your role can't view this console.",
};

export const UnauthorizedFrame = ({
  reason,
}: {
  reason: UnauthorizedReason;
}) => (
  <div className={styles.card} data-testid="flp-unauthorized">
    <div className={styles.section}>
      <span className={styles.strong}>Restricted access.</span>{' '}
      <span className={styles.muted}>{UNAUTHORIZED_REASON_COPY[reason]}</span>
    </div>
  </div>
);

// Safety 'unknown': never implies safety. GRAVY-10373 Task 8 — the caller renders this ABOVE the
// console rather than instead of it, so the operator keeps reading what the pilot is doing; only
// configuration is locked. Escalating mode changes are still disabled up in the header, and STOP
// REMAINS AVAILABLE — a safety failure must not take away the safer action.
export const SafetyUnknownTakeover = ({ reason }: { reason: string }) => (
  <div
    className={`${styles.card} ${styles.attention}`}
    data-testid="flp-safety-unknown"
    role="status"
  >
    <div className={styles.section}>
      <div className={styles.strong}>Safety status unavailable</div>
      <div
        className={`${styles.muted} ${styles.small}`}
        style={{ marginTop: 4 }}
      >
        {reason}
      </div>
      <div
        className={`${styles.muted} ${styles.small}`}
        style={{ marginTop: 8 }}
      >
        Configuration changes are disabled until safety evidence is available.
        Stop First Responder remains available.
      </div>
    </div>
  </div>
);

// Safety 'breached': the STOP band and its reasons, rendered by the caller ABOVE the console
// (Task 8) — the recorded runs behind it are exactly what an operator needs to read when an
// invariant has been broken. Configuration is locked; Stop stays available.
export const BreachTakeover = ({ reasons }: { reasons: string[] }) => (
  <div data-testid="flp-breach" role="alert">
    <div className={styles.safetyStop}>
      STOP — safety invariant breached
      <span style={{ display: 'block', marginTop: 4 }}>
        First Responder must not act until this is resolved.
      </span>
    </div>
    <div className={`${styles.card} ${styles.safetyBreach}`}>
      <div className={styles.section}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {reasons.map((r) => (
            <li key={r} className={styles.strong}>
              {r}
            </li>
          ))}
        </ul>
        <div
          className={`${styles.muted} ${styles.small}`}
          style={{ marginTop: 8 }}
        >
          Configuration changes are disabled. Stop remains available.
        </div>
      </div>
    </div>
  </div>
);
